// ResetPasswordPage — trang đặt mật khẩu mới từ link trong email. Dùng chung Core +
// platform + SaaS.
//
// Host responsibility (HOST_CONTRACT.md):
//   `@host/auth.useAuth()` phải có:
//     - resetPassword(token, newPassword) => Promise<void>          [bắt buộc]
//     - verifyResetToken(token) => Promise<{ valid, reason, email }> [tuỳ chọn]
//   Có `verifyResetToken` thì trang báo "link hết hạn" NGAY khi mở, thay vì để người
//   dùng gõ xong mật khẩu mới rồi mới báo lỗi. Thiếu thì trang vẫn chạy (bỏ bước kiểm).
//
// Token nằm ở query `?token=`. KHÔNG đặt token vào path để nó không lọt vào log
// nginx/analytics theo route pattern, và không hiện trên thanh tiêu đề trang.
//
// ⚠️ i18n: cụm `pages-auth`.

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Lock, Eye, EyeOff, Loader2, ShieldCheck, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useAuth } from '@host/auth';
import LoginShell from './LoginShell.jsx';
import '../../i18n/c/pages-auth.js';

// Phải khớp PASSWORD_MIN_LENGTH của @cnv/server-shared/auth/passwordReset.js.
// Lệch xuống thì FE cho bấm rồi BE chặn — người dùng thấy lỗi vô cớ.
const MIN_LEN = 8;

export default function ResetPasswordPage({
  logoSrc,
  logoAlt,
  brandName,
  tagline,
  variant = 'pink',
  loginHref = '/login',
  forgotHref = '/forgot-password',
  onLocaleChange,
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useLingui();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  // Rút hàm ra biến TRƯỚC effect: `auth` là object mới mỗi render (host dựng inline trong
  // provider), đưa nguyên nó vào deps là vòng lặp render vô hạn — đúng vết useChat cũ.
  const verifyResetToken = auth?.verifyResetToken;

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // 'checking' | 'ok' | 'bad' — 'ok' cũng là trạng thái khi host không có verifyResetToken.
  const [checking, setChecking] = useState(token ? 'checking' : 'bad');
  const [badReason, setBadReason] = useState(token ? null : 'missing');

  useEffect(() => {
    if (!token) return undefined;
    if (typeof verifyResetToken !== 'function') {
      setChecking('ok');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await verifyResetToken(token);
        if (cancelled) return;
        if (r?.valid) setChecking('ok');
        else { setBadReason(r?.reason || 'invalid'); setChecking('bad'); }
      } catch {
        // Lỗi mạng khi kiểm TRƯỚC không nên chặn người dùng: cho gõ mật khẩu, submit
        // sẽ là lần kiểm thật. Chặn ở đây = link tốt mà báo hỏng vì mạng chớp.
        if (!cancelled) setChecking('ok');
      }
    })();
    return () => { cancelled = true; };
    // `t` của useLingui KHÔNG vào deps (đổi mỗi render → loop). Xem docs/I18N.md.
  }, [token, verifyResetToken]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (password.length < MIN_LEN) {
      toast.error(t`Mật khẩu phải có ít nhất ${MIN_LEN} ký tự`, { id: 'reset-err' });
      return;
    }
    if (password !== confirm) {
      toast.error(t`Hai mật khẩu không giống nhau`, { id: 'reset-err' });
      return;
    }
    if (typeof auth?.resetPassword !== 'function') {
      toast.error(t`Chức năng chưa được cài đặt trên hệ thống này`, { id: 'reset-err' });
      return;
    }
    setSubmitting(true);
    try {
      await auth.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      const code = data?.error?.code;
      const issue = data?.error?.details?.issue || data?.error?.issue;
      // Tài khoản quản trị bị đòi 10 ký tự (kernel passwordPolicy, Work gửi `details.min_length`) —
      // báo cứng MIN_LEN là người gõ 9 ký tự đọc «ít nhất 8» mà vẫn bị từ chối.
      const minLen = Number(data?.error?.details?.min_length) || MIN_LEN;
      if (code === 'reset_token_invalid' || code === 'reset_token_expired' || code === 'reset_token_used') {
        setBadReason(code.replace('reset_token_', ''));
        setChecking('bad');
      } else if (issue === 'too_short') {
        toast.error(t`Mật khẩu phải có ít nhất ${minLen} ký tự`, { id: 'reset-err' });
      } else if (issue === 'too_common') {
        toast.error(t`Mật khẩu này quá phổ biến, hãy chọn mật khẩu khác`, { id: 'reset-err' });
      } else if (issue === 'too_long') {
        toast.error(t`Mật khẩu quá dài`, { id: 'reset-err' });
      } else if (issue === 'contains_email') {
        toast.error(t`Mật khẩu không được chứa tên email của bạn.`, { id: 'reset-err' });
      } else if (status === 429) {
        toast.error(t`Quá nhiều yêu cầu, vui lòng thử lại sau`, { id: 'reset-err' });
      } else if (status >= 500) {
        toast.error(t`Hệ thống đang lỗi, thử lại sau`, { id: 'reset-err' });
      } else if (typeof data?.message === 'string' && data.message.trim()) {
        // Lý do máy chủ đã viết cho người đọc (Work: `userMessage` đã dịch theo ngôn ngữ; Core: câu
        // tiếng Việt) — issue mới của kernel chưa có nhánh ở đây vẫn báo đúng thay vì câu chung.
        toast.error(data.message, { id: 'reset-err' });
      } else {
        toast.error(t`Không đặt lại được mật khẩu, vui lòng thử lại`, { id: 'reset-err' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const shellProps = {
    logoSrc, logoAlt, brandName, tagline, variant, onLocaleChange,
  };

  // ── Đang kiểm link ─────────────────────────────────────────────────────────
  if (checking === 'checking') {
    return (
      <LoginShell {...shellProps} subtitle={t`Đặt lại mật khẩu`} subtitleHint="">
        <div className="flex items-center justify-center py-6 text-sm text-slate-500">
          <Loader2 size={16} className="mr-2 animate-spin" />
          <Trans>Đang kiểm tra liên kết…</Trans>
        </div>
      </LoginShell>
    );
  }

  // ── Link hỏng / hết hạn / đã dùng ──────────────────────────────────────────
  if (checking === 'bad') {
    const REASON_TEXT = {
      missing: t`Liên kết không có mã xác thực. Hãy mở lại link trong email, hoặc xin liên kết mới.`,
      expired: t`Liên kết đã hết hạn. Liên kết chỉ có hiệu lực 30 phút.`,
      used: t`Liên kết này đã được dùng để đặt lại mật khẩu. Mỗi liên kết chỉ dùng được một lần.`,
      invalid: t`Liên kết không hợp lệ. Có thể bạn đã xin liên kết mới hơn — hãy dùng email mới nhất.`,
      not_found: t`Liên kết không hợp lệ. Có thể bạn đã xin liên kết mới hơn — hãy dùng email mới nhất.`,
    };
    return (
      <LoginShell
        {...shellProps}
        subtitle={t`Liên kết không dùng được`}
        subtitleHint=""
        footer={(
          <Link to={loginHref} className="inline-flex items-center gap-1.5 text-blue-600 font-medium hover:underline">
            <ArrowLeft size={14} />
            <Trans>Về trang đăng nhập</Trans>
          </Link>
        )}
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50">
            <AlertTriangle size={26} className="text-amber-600" />
          </div>
          <p className="text-sm leading-relaxed text-slate-600">
            {REASON_TEXT[badReason] || REASON_TEXT.invalid}
          </p>
          <Link
            to={forgotHref}
            className="mt-5 block w-full rounded-lg bg-blue-600 py-2.5 text-center text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            <Trans>Xin liên kết mới</Trans>
          </Link>
        </div>
      </LoginShell>
    );
  }

  // ── Đổi xong ───────────────────────────────────────────────────────────────
  if (done) {
    return (
      <LoginShell {...shellProps} subtitle={t`Đã đổi mật khẩu`} subtitleHint="">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50">
            <ShieldCheck size={26} className="text-emerald-600" />
          </div>
          <p className="text-sm leading-relaxed text-slate-600">
            <Trans>
              Mật khẩu mới đã được lưu. Mọi thiết bị đang đăng nhập đã bị đăng xuất để đảm bảo
              an toàn.
            </Trans>
          </p>
          <button
            type="button"
            onClick={() => navigate(loginHref)}
            className="mt-5 w-full rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            <Trans>Đăng nhập</Trans>
          </button>
        </div>
      </LoginShell>
    );
  }

  // ── Form đặt mật khẩu mới ──────────────────────────────────────────────────
  const tooShort = password.length > 0 && password.length < MIN_LEN;
  const mismatch = confirm.length > 0 && password !== confirm;

  return (
    <LoginShell
      {...shellProps}
      subtitle={t`Đặt mật khẩu mới`}
      subtitleHint={t`Chọn mật khẩu mới cho tài khoản của bạn.`}
      footer={(
        <Link to={loginHref} className="inline-flex items-center gap-1.5 text-blue-600 font-medium hover:underline">
          <ArrowLeft size={14} />
          <Trans>Về trang đăng nhập</Trans>
        </Link>
      )}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">
            <Trans>Mật khẩu mới</Trans>
          </label>
          <div className="relative">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type={showPwd ? 'text' : 'password'}
              required
              autoFocus
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`w-full rounded-lg border py-2.5 pl-10 pr-10 text-sm outline-none focus:ring-2 ${
                tooShort
                  ? 'border-amber-400 focus:border-amber-500 focus:ring-amber-400'
                  : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPwd((s) => !s)}
              aria-label={showPwd ? t`Ẩn mật khẩu` : t`Hiện mật khẩu`}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <p className={`mt-1.5 text-xs ${tooShort ? 'text-amber-600' : 'text-slate-500'}`}>
            <Trans>Tối thiểu {MIN_LEN} ký tự</Trans>
          </p>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">
            <Trans>Nhập lại mật khẩu mới</Trans>
          </label>
          <div className="relative">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type={showPwd ? 'text' : 'password'}
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={`w-full rounded-lg border py-2.5 pl-10 pr-3 text-sm outline-none focus:ring-2 ${
                mismatch
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-400'
                  : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
            />
          </div>
          {mismatch && (
            <p className="mt-1.5 text-xs text-red-600">
              <Trans>Hai mật khẩu không giống nhau</Trans>
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
          {submitting ? <Trans>Đang lưu...</Trans> : <Trans>Đặt mật khẩu mới</Trans>}
        </button>
      </form>
    </LoginShell>
  );
}
